'use client';
import React, { useEffect, useState } from 'react';
import { AlertTriangle, XCircle, Info, ChevronRight } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface PolicyAlert {
  id: string;
  severity: 'critical' | 'warning' | 'info';
  title: string;
  description: string;
  org?: string;
  time: string;
}

const severityConfig = {
  critical: { icon: XCircle, iconClass: 'text-danger', bg: 'bg-danger/5 border-danger/20' },
  warning: { icon: AlertTriangle, iconClass: 'text-warning', bg: 'bg-warning/5 border-warning/20' },
  info: { icon: Info, iconClass: 'text-info', bg: 'bg-info/5 border-info/20' },
};

export default function PolicyAlertsPanel() {
  const [alerts, setAlerts] = useState<PolicyAlert[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const res = await fetch((process.env.NEXT_PUBLIC_API_URL || '') + '/api/policy-alerts');
        if (res.ok) {
          const data = await res.json();
          setAlerts(data.alerts || []);
        }
      } catch (err) {
        console.error('Failed to fetch policy alerts:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3.5 border-b border-border">
        <div className="flex items-center gap-2">
          <AlertTriangle size={15} className="text-danger" />
          <h3 className="text-sm font-600 text-foreground">Policy Alerts</h3>
          {alerts.length > 0 && (
            <span className="badge-danger text-[10px] font-700 px-1.5 py-0.5 rounded-full">{alerts.length}</span>
          )}
        </div>
        <button className="text-[11px] text-primary hover:underline">View all</button>
      </div>
      {loading ? (
        <div className="p-4 space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={`skel-alert-${i}`} className="h-14 bg-muted rounded animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="divide-y divide-border">
          {alerts.map((alert) => {
            const cfg = severityConfig[alert.severity] || severityConfig.info;
            const Icon = cfg.icon;
            return (
              <div key={alert.id} className="px-4 py-3 hover:bg-muted/50 transition-colors cursor-pointer group">
                <div className="flex items-start gap-2.5">
                  <Icon size={14} className={`${cfg.iconClass} mt-0.5 shrink-0`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-[12px] font-600 text-foreground leading-snug">{alert.title}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug line-clamp-2">{alert.description}</p>
                    <div className="flex items-center gap-1.5 mt-1">
                      {alert.org && (
                        <span className="text-[10px] badge-neutral px-1.5 py-0.5 rounded truncate max-w-[120px]">{alert.org}</span>
                      )}
                      <span className="text-[10px] text-muted-foreground">{alert.time}</span>
                    </div>
                  </div>
                  <ChevronRight size={12} className="text-muted-foreground group-hover:text-foreground transition-colors shrink-0 mt-1" />
                </div>
              </div>
            );
          })}
          {alerts.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">No active policy alerts</div>
          )}
        </div>
      )}
    </div>
  );
}